import React, { useEffect, useRef, useState } from 'react';
import { FaEllipsisH } from 'react-icons/fa';
import SvgCheck from '@icons/check.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import { useSecurityMode } from '@components/security/SecurityModeContext';
import { hasInspectableMassAction, isMassActionVisible, massActionSecuredConfig, shownMassActions } from './massActionSecurity';
import type { MassActionConfig } from './massActionTypes';

export type { MassActionConfig };
export { isMassActionVisible, hasInspectableMassAction };

interface MassActionButtonsProps<T> {
  massActionConfig: MassActionConfig<T>[];
  selectedObjects: T[];
  isMobile: boolean;
  /** Table identity, so that fallback area ids stay unique when a page holds more than one table. */
  idPrefix: string;
}

export const MassActionButtons = <T,>({ massActionConfig, selectedObjects, isMobile, idPrefix }: MassActionButtonsProps<T>) => {
  const { tr } = useTranslationLgs();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileMenuOpen]);

  const { enabled: securityMode } = useSecurityMode();

  const shownActions = shownMassActions(massActionConfig, selectedObjects, securityMode);

  const securedFor = (item: MassActionConfig<T>, idx: number, disabled: boolean) =>
    massActionSecuredConfig(item, { idx, idPrefix, selected: selectedObjects, securityMode, disabled });

  /**
   * A disabled action with its explanation attached.
   *
   * The tooltip hangs on a wrapper rather than on the button: a disabled button fires no mouse
   * events, so a tooltip targeting it would never appear — which is exactly when the user most
   * needs it. `inline-flex` keeps the wrapper the size of the button so the tooltip points at it.
   */
  const withReason = (item: MassActionConfig<T>, disabled: boolean, button: React.ReactNode, key: string) => {
    // An empty selection is why most disabled mass actions are disabled, and no action should have
    // to say so itself — the default covers every table at once. An action that is disabled for its
    // own reasons still owns the explanation, so its disabledReason wins.
    const emptySelection = selectedObjects.length === 0 ? tr('RingilDataTable.selectRowsFirst', 'Nejprve vyberte řádky') : undefined;
    const reason = disabled ? (item.disabledReason?.(selectedObjects) ?? emptySelection) : undefined;
    if (!reason) return button;

    return (
      <span key={key} id={key} className={'inline-flex cursor-help'}>
        {button}
        <CustomTooltip targetSelector={`#${key}`} content={reason} />
      </span>
    );
  };

  if (isMobile) {
    return (
      <div className="relative" ref={mobileMenuRef}>
        <button
          aria-label="Mass actions"
          className="bg-transparent border-none p-2 cursor-pointer text-[#fe5000] flex items-center"
          onClick={() => setMobileMenuOpen(open => !open)}
        >
          <FaEllipsisH size={20} />
        </button>
        {mobileMenuOpen && (
          <div className="absolute top-full right-0 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.15)] rounded z-[1000] min-w-[200px] p-2 flex flex-col gap-1">
            {shownActions.map((item, idx) => {
              const disabled = item.disabled ? item.disabled(selectedObjects) : selectedObjects.length === 0;
              return withReason(
                item,
                disabled,
                <RingilButton
                  key={idx}
                  label={item.label}
                  icon={item.icon}
                  mode="link"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    item.onClick();
                  }}
                  disabled={disabled}
                  secured={securedFor(item, idx, disabled)}
                />,
                `massAction-mobile-${idx}`,
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mass-action-buttons-container flex items-center gap-2">
      {shownActions.map((item, idx) => {
        const disabled = item.disabled ? item.disabled(selectedObjects) : selectedObjects.length === 0;
        return withReason(
          item,
          disabled,
          <RingilButton
            key={idx}
            label={item.label}
            icon={item.icon}
            mode="filled-rectangle"
            onClick={() => item.onClick()}
            disabled={disabled}
            secured={securedFor(item, idx, disabled)}
          />,
          `massAction-${idx}`,
        );
      })}
    </div>
  );
};

interface TriStateCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  onChange: (next: boolean) => void;
}

const TriStateCheckbox: React.FC<TriStateCheckboxProps> = ({ checked, indeterminate, onChange }) => {
  const active = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      onClick={() => onChange(!active)}
      className={`w-[18px] h-[18px] rounded border-2 border-solid flex items-center justify-center transition-colors p-0 cursor-pointer ${
        active ? 'bg-content-brand border-content-brand text-white' : 'bg-white border-[#D1D5DB] hover:border-content-brand'
      }`}
    >
      {indeterminate ? <span className="block w-[10px] h-[2px] bg-white" /> : checked ? <SvgCheck /> : null}
    </button>
  );
};

interface ActionPanelProps<T> {
  selectedCount: number;
  totalCount: number;
  onSelectAllChange: (checked: boolean) => void;
  isMobile: boolean;
  massActionConfig: MassActionConfig<T>[];
  selectedObjects: T[];
  floatingMassActions?: boolean;
  /** Whether the selection + mass-action controls should be shown on the left. */
  hasMassActions?: boolean;
  /** Optional content (e.g. export) rendered right-aligned in the bar. */
  rightSlot?: React.ReactNode;
  /** Table identity, so that fallback area ids stay unique when a page holds more than one table. */
  idPrefix: string;
}

export const ActionPanel = <T,>({
  selectedCount,
  totalCount,
  onSelectAllChange,
  isMobile,
  massActionConfig,
  selectedObjects,
  floatingMassActions = false,
  hasMassActions = true,
  rightSlot,
  idPrefix,
}: ActionPanelProps<T>) => {
  const { tr } = useTranslationLgs();

  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const indeterminate = selectedCount > 0 && selectedCount < totalCount;

  const baseClasses = 'bg-content-quaternary flex flex-wrap items-center content-center self-stretch justify-between gap-4 py-3 px-6';
  const floatingClasses = floatingMassActions ? 'sticky top-0 z-10' : '';

  return (
    <div className={`${baseClasses} ${floatingClasses}`}>
      {hasMassActions ? (
        <div className="flex items-center gap-2">
          <TriStateCheckbox checked={allSelected} indeterminate={indeterminate} onChange={onSelectAllChange} />
          <span className="whitespace-nowrap">
            {selectedCount} {tr('RingilDataTable.selected', 'selected')}
          </span>
          <div className="flex items-center gap-2">
            <MassActionButtons massActionConfig={massActionConfig} selectedObjects={selectedObjects} isMobile={isMobile} idPrefix={idPrefix} />
          </div>
        </div>
      ) : (
        <div />
      )}
      {rightSlot && <div className="flex items-center gap-2">{rightSlot}</div>}
    </div>
  );
};
